/**
 * @file BaseChildReportService.ts
 * @description 底座子设备事件辅助服务，复用子鼠标状态解析和连接事件抛出逻辑。
 */

import type { BaseDevice } from '../../../../runtime/BaseDevice'
import type { BaseChildReport, BaseConnectedMouse } from '../../../../types/base'
import {
  parseBaseChildReport,
  toBytes,
} from '../../../../chips/shared/protocols/base-command/command-io'
import type { BaseDockProfile } from '../../../../chips/shared/capabilities/base-command/profile'
import { DeviceFactory } from '../../../../runtime/DeviceFactory'
import { findRegisteredManifest } from '../../../../manifests/DeviceManifestRegistry'
import { BaseDockEvents } from '../../../../events/BaseDockEvents'
import { HidChannelProxy } from '../../../../channels/hid/proxy/HidChannelProxy'

/** 底座子鼠标 report/事件辅助。 */
export class BaseChildReportService {
  private currentChildDevice: BaseDevice | null = null
  private currentChildChannel: HidChannelProxy | null = null
  private currentChildIdentityKey: string | null = null
  private currentChildOnline = false
  /** 防止高频 0xb0 状态上报并发触发多次 VID/PID 透传查询。 */
  private identityQueryInFlight = false

  /** 依赖父底座设备和协议 profile，避免服务自己理解具体型号差异。 */
  constructor(
    private readonly device: BaseDevice,
    private readonly profile: BaseDockProfile,
  ) {}

  /** 解析底座 0xb0 子鼠标上报，隔离不同底座协议族的偏移差异。 */
  public parse(data: DataView): BaseChildReport {
    return parseBaseChildReport(toBytes(data), this.profile.childReportOffset)
  }

  /** 将底座子鼠标 report 同时派发给鼠标驱动和 Web 事件监听方。 */
  public emitReport(data: DataView): void {
    const report = this.parse(data)
    this.currentChildChannel?.emitInputReport(0x07, this.toMouseReportData(data))
    this.device.emit(BaseDockEvents.ChildReport, report)
  }

  public emitRawMouseReport(data: DataView): void {
    this.currentChildChannel?.emitInputReport(0x07, toBytes(data))
  }

  /**
   * 0xb0 状态上报意味着子鼠标链路可用，按当前状态决定是否补齐身份/在线态。
   *
   * - 已在线：不再透传查询（避免每次状态上报都读 VID/PID）
   * - 已识别身份但离线（例如刚收到 0xb3）：直接恢复在线，不必再查
   * - 尚未识别（例如只收到 b0、漏了配对事件）：透传读取 VID/PID，并防重入
   */
  public ensureOnlineFromStatusReport(): void {
    if (this.hasOnlineChild()) return

    if (this.currentChildDevice && this.currentChildIdentityKey) {
      const mouse = parseIdentityKey(this.currentChildIdentityKey)
      if (mouse) {
        this.markCurrentChildOnline(mouse)
        return
      }
    }

    if (this.identityQueryInFlight) return
    this.identityQueryInFlight = true
    void this.emitCurrentConnectedMouse().finally(() => {
      this.identityQueryInFlight = false
    })
  }

  /** 在底座提示“有子鼠标”后，主动读取 VID/PID 来补齐真实鼠标身份。 */
  public async emitCurrentConnectedMouse(): Promise<void> {
    // 配对成功上报只告诉我们“有子鼠标”，真实 VID/PID 仍需通过底座透传命令读取。
    const mouse = await this.device.features.baseSettings?.getConnectedMouse()
    if (mouse) await this.syncConnectedMouse(mouse)
  }

  /** 同步当前底座子鼠标身份，并维护 base.subDevices 里的逻辑鼠标实例。 */
  public async syncConnectedMouse(mouse: BaseConnectedMouse): Promise<BaseDevice | null> {
    const identity = normalizeIdentity(mouse)
    if (!identity) {
      // 关键：读不到 VID/PID 只说明本次透传探测失败，不代表底座已经取消配对。
      // 鼠标离开底座时可能暂时无法读取身份，但展示列表仍应保留已配对的鼠标。
      return this.currentChildDevice
    }

    const identityKey = createIdentityKey(identity)
    const isSameChild = this.currentChildIdentityKey === identityKey

    if (!isSameChild) {
      this.detachCurrentChildDevice()
      this.currentChildIdentityKey = identityKey
      const childDevice = this.createMouseDevice(identity)
      if (!childDevice) {
        this.device.emit(BaseDockEvents.ChildIdentityConnected, mouse)
        return null
      }

      this.currentChildDevice = childDevice.device
      this.currentChildChannel = childDevice.channel
      this.attachCurrentChildDevice(this.currentChildDevice)
      this.currentChildOnline = true
      await this.currentChildDevice.open()
      this.device.emit(BaseDockEvents.ChildDeviceConnected, this.currentChildDevice)
      this.device.emit(BaseDockEvents.ChildOnlineChanged, {
        device: this.currentChildDevice,
        online: true,
      })
      this.device.emit(BaseDockEvents.ChildDisplayChanged, this.device.subDevices)
      this.device.emit(BaseDockEvents.ChildIdentityConnected, mouse)
    } else {
      this.markCurrentChildOnline(mouse)
    }

    return this.currentChildDevice
  }

  /** 底座上报子鼠标断开时，只更新在线态，不删除仍然配对的子设备。 */
  public disconnectCurrentMouse(): void {
    const childDevice = this.currentChildDevice
    if (!childDevice) {
      this.currentChildIdentityKey = null
      this.device.emit(BaseDockEvents.ChildIdentityDisconnected)
      return
    }

    this.markCurrentChildOffline()
  }

  /** 底座物理断开或换绑时，才真正从展示树移除子设备实例。 */
  public removeCurrentMouse(): void {
    const childDevice = this.currentChildDevice
    if (!childDevice) return

    this.detachCurrentChildDevice()
    this.device.emit(BaseDockEvents.ChildDisplayChanged, this.device.subDevices)
  }

  /**
   * 配对成功后清除子设备缓存。
   *
   * 当父底座上报 b1 配对成功时，底座会让子设备初始化配置（例如恢复出厂参数等），
   * 此时子设备硬件上的配置已变更，需要清除缓存，确保后续读取能重新从硬件获取最新数据。
   */
  public clearCurrentChildCache(): void {
    this.currentChildDevice?.cache.clearAll()
  }

  /** 当前是否有子设备正在在线（已连接）状态。 */
  public hasOnlineChild(): boolean {
    return this.currentChildOnline && this.currentChildDevice !== null
  }

  /** 当前是否已识别出子鼠标身份（无论是否在线）。 */
  public hasDetectedChild(): boolean {
    return this.currentChildIdentityKey !== null
  }

  /** 清理当前子鼠标驱动引用，用于换绑或父底座断开后的资源释放。 */
  private detachCurrentChildDevice(): void {
    const childDevice = this.currentChildDevice
    if (!childDevice) return

    this.detachChildDevice(childDevice)
    this.currentChildDevice = null
    this.currentChildChannel = null
    this.currentChildIdentityKey = null
    this.currentChildOnline = false
    childDevice.close().catch((error) => {
      console.warn('[BaseChildReportService] Failed to close child device', error)
    })
  }

  /** 根据 VID/PID 复用已注册 manifest，创建走 HID 代理视图的真实鼠标驱动。 */
  private createMouseDevice(identity: { vendorId: number; productId: number }): {
    device: BaseDevice
    channel: HidChannelProxy
  } | null {
    const manifest = findRegisteredManifest(identity.vendorId, identity.productId)
    if (!manifest || manifest.type !== 'mouse') {
      console.warn(
        `[BaseChildReportService] Mouse manifest not found: VID 0x${identity.vendorId.toString(16)}, PID 0x${identity.productId.toString(16)}`,
      )
      return null
    }

    manifest.hardware.currentPid = identity.productId
    const channel = new HidChannelProxy(this.device.channel, {
      deviceInfo: {
        vendorId: identity.vendorId,
        productId: identity.productId,
        productName: manifest.model,
      },
    })
    const device = DeviceFactory.create(manifest, channel)
    // 关键：底座代理出的子鼠标必须继承父会话的固件源与版本策略。
    device.__setFirmwareBaseUrl(this.device.getFirmwareBaseUrl())
    device.__setFirmwareVersionPolicy(this.device.getFirmwareVersionPolicy())
    // 关键：底座子鼠标不是 USB 直连，鼠标 EEPROM/配置命令必须使用 0xa5 无线透传前缀。
    // 这里显式设置运行态连接类型，避免依赖 productName 包含 Receiver 这种隐式约定。
    device.connectionType = 0xa5
    device.shareCommandQueueWith(this.device)
    setParentDevice(device, this.device)

    return { device, channel }
  }

  /** 已有子鼠标重新被底座探测到时，只恢复在线态和展示状态。 */
  private markCurrentChildOnline(mouse: BaseConnectedMouse): void {
    const childDevice = this.currentChildDevice
    if (!childDevice || this.currentChildOnline) return

    this.currentChildOnline = true
    childDevice.emit('connected')
    this.device.emit(BaseDockEvents.ChildIdentityConnected, mouse)
    this.device.emit(BaseDockEvents.ChildOnlineChanged, { device: childDevice, online: true })
    this.device.emit(BaseDockEvents.ChildDisplayChanged, this.device.subDevices)
  }

  /** 子鼠标链路离线时保留配对实例，只通知在线态变化。 */
  private markCurrentChildOffline(): void {
    const childDevice = this.currentChildDevice
    if (!childDevice || !this.currentChildOnline) return

    // 关键：0xb3 表示当前链路离线，不表示取消配对；因此不 detachSubDevice。
    this.currentChildOnline = false
    childDevice.emit('disconnected')
    this.device.emit(BaseDockEvents.ChildIdentityDisconnected, childDevice)
    this.device.emit(BaseDockEvents.ChildOnlineChanged, { device: childDevice, online: false })
    this.device.emit(BaseDockEvents.ChildDisplayChanged, this.device.subDevices)
  }

  /** 去掉底座命令头，只把鼠标原始 report 数据交给鼠标驱动解析。 */
  private toMouseReportData(data: DataView): Uint8Array {
    const bytes = toBytes(data)
    return bytes.slice(this.profile.childReportOffset)
  }

  /** 建立父子展示关系；该列表只表达归属，不表达物理连接。 */
  private attachCurrentChildDevice(childDevice: BaseDevice): void {
    const subDevices = this.device.subDevices
    if (!subDevices || subDevices.some((item) => item === childDevice)) return

    // 关键：父子设备关系只是展示层的设备树，连接在线态由本服务单独维护。
    subDevices.push(childDevice)
  }

  /** 从父设备展示树中移除子设备，并清空反向 parentDevice 引用。 */
  private detachChildDevice(childDevice: BaseDevice): void {
    const subDevices = this.device.subDevices
    if (!subDevices) return

    const index = subDevices.findIndex((item) => item === childDevice)
    if (index >= 0) {
      subDevices.splice(index, 1)
    }
    setParentDevice(childDevice, undefined)
    this.device.emit(BaseDockEvents.ChildDeviceDisconnected, childDevice)
  }
}

/** 规整底座返回的 VID/PID，非法值按“本次身份读取失败”处理。 */
function normalizeIdentity(
  mouse: BaseConnectedMouse,
): { vendorId: number; productId: number } | null {
  const vendorId = Number(mouse.vendorId)
  const productId = Number(mouse.productId)
  if (!Number.isFinite(vendorId) || vendorId <= 0) return null
  if (!Number.isFinite(productId) || productId <= 0) return null
  return { vendorId, productId }
}

/** 父子设备匹配只需要 VID/PID，避免把运行态字段带入身份判断。 */
function createIdentityKey(identity: { vendorId: number; productId: number }): string {
  return `${identity.vendorId}:${identity.productId}`
}

/** 从内部身份键还原 VID/PID，供离线后凭 0xb0 直接恢复在线态。 */
function parseIdentityKey(identityKey: string): BaseConnectedMouse | null {
  const [vendorRaw, productRaw] = identityKey.split(':')
  const vendorId = Number(vendorRaw)
  const productId = Number(productRaw)
  if (!Number.isFinite(vendorId) || vendorId <= 0) return null
  if (!Number.isFinite(productId) || productId <= 0) return null
  return { vendorId, productId }
}

/** parentDevice 是展示树关系，运行时通过窄化写入，避免扩大 BaseDevice 公共模型。 */
function setParentDevice(device: BaseDevice, parentDevice: BaseDevice | undefined): void {
  ;(device as BaseDevice & { parentDevice?: BaseDevice }).parentDevice = parentDevice
}
